import React, { useState, useMemo } from 'react';
import { Text, H4, Label } from '@amzn/stencil-react-components/text';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Flex, Spacer } from '@amzn/stencil-react-components/layout';
import {
  REQUEST_HELP_CONFIG,
  RequestHelpSection,
  RequestHelpWorkflow,
  RequestHelpWorkflowInput
} from '../constants/requestHelpWorkflows';
import { Input } from '@amzn/stencil-react-components/form';
import { useGlobalContext } from '../hooks';
import { useErrorHandler } from 'react-error-boundary';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { useTranslation } from 'react-i18next';
import { metricSender } from '../util/metric_sender';

/**
 * The RequestHelp UI is implemented by a state machine.
 * States are transitioned as a result of the user clicking buttons
 * and from the configuration defined in REQUEST_HELP_CONFIG.
 */
enum STATE {
  INITIAL = 'Initial',
  WORKFLOW_SELECTION = 'WorkflowSelection',
  FAQ_RENDER = 'FAQRender',
  INPUT_RETRIEVAL = 'InputRetrieval',
  ACTION_GENERATION = 'ActionGeneration',
  DONE = 'Done'
}

interface RequestHelpWorkflowRendererProps {
  onRequestCreated: () => void;
  openActionTitles: Set<string>;
}

export const RequestHelpWorkflowRenderer = (props: RequestHelpWorkflowRendererProps) => {
  const { t } = useTranslation();
  const { backendServiceClient, userToken, userDetails } = useGlobalContext();
  const [selectedSection, setSelectedSection] = useState(undefined as RequestHelpSection|undefined);
  const [selectedWorkflow, setSelectedWorkflow] = useState(undefined as RequestHelpWorkflow|undefined);
  const [inputValues, setInputValues] = useState(new Map<string, string>());

  const [state, setState] = useState(STATE.INITIAL);
  const [previousStates, setPreviousStates] = useState([] as STATE[]);

  const [finalStateTitle, setFinalStateTitle] = useState("");
  const [finalStateText, setFinalStateText] = useState("");
  const [isActionSubmitting, setActionSubmitting] = useState(false);
  const handleError = useErrorHandler();

  const moveState = (newState: STATE) => {
    setPreviousStates([...previousStates, state]);
    setState(newState);
  }

  const generateHelpRequest = async () => {
    setActionSubmitting(true);
    const helpType = selectedWorkflow!.helpType;
    let actionDetails;
    if (selectedWorkflow!.actionDetailsGenerator != undefined) {
      actionDetails = selectedWorkflow!.actionDetailsGenerator(inputValues);
    }
    let supplementalUrls;
    if (selectedWorkflow!.supplementalUrlsGenerator != undefined) {
      supplementalUrls = selectedWorkflow!.supplementalUrlsGenerator(userDetails);
    }

    try {
      await backendServiceClient.createRequestHelpAction(
        userToken,
        userDetails.employeeId,
        userDetails.login,
        userDetails.warehouseId,
        userDetails.managerLogin,
        helpType,
        actionDetails,
        supplementalUrls,
      );
    } catch (error) {
      handleError(new Error(t('createRequestFailed')));
    }
    metricSender.generateHelpMeMetrics(userDetails, selectedWorkflow!.helpType, true);

    setActionSubmitting(false);
  }

  const sectionButtons = useMemo(() => {
    return REQUEST_HELP_CONFIG.map((section: RequestHelpSection, index: number) => {
      return (
        <Button data-cy={`section-${section.name}`} key={index} onClick={() => {
          setSelectedSection(section);

          moveState(STATE.WORKFLOW_SELECTION);
        }}>
          {t(section.name)}
        </Button>
      );
    })
  }, []);

  const workflowButtons = useMemo(() => {
    if (selectedSection == undefined) {
      return [];
    } else {
      return selectedSection.workflows.map((workflow: RequestHelpWorkflow, index: number) => {
        return (
          <Button
            data-cy={`workflow-${workflow.text}`}
            key={index}
            disabled={props.openActionTitles.has(workflow.title)}
            onClick={() => {
              setSelectedWorkflow(workflow);

              if (workflow.faqSet != undefined) {
                moveState(STATE.FAQ_RENDER);
              } else if (workflow.inputs != undefined) {
                moveState(STATE.INPUT_RETRIEVAL);
              } else {
                moveState(STATE.ACTION_GENERATION);
              }
            }}
          >
            {t(workflow.text)}
          </Button>
        );
      });
    }
  }, [selectedSection]);

  const inputs = useMemo(() => {
    return (selectedWorkflow?.inputs || []).map((input: RequestHelpWorkflowInput, index: number) => {
      return (
        <div key={index}>
          <Label htmlFor={`input-${input.id}`}>{t(input.text)}</Label>
          <Input
            id={`input-${input.id}`}
            type="text"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const newInputValues = new Map(inputValues);
              newInputValues.set(input.id, event.target.value);
              setInputValues(newInputValues);
            }}
          />
        </div>
      );
    });
  }, [selectedWorkflow]);

  /**
   * UI to display for each state
   */
  let content;
  if (state === STATE.INITIAL) {
    content = (
      <>
        <H4>{t('initialRequestHelpQuestion')}</H4>
        {sectionButtons}
      </>
    );
  } else if (state === STATE.WORKFLOW_SELECTION) {
    content = (
      <>
        <H4>{t('initialRequestHelpQuestion')}</H4>
        {workflowButtons}
        <Flex gridGap="S200" flexDirection="row" justifyContent="left">
          <Button onClick={() => {
            setState(previousStates.pop()!);
          }}>
            {t('back')}
          </Button>
        </Flex>
      </>
    );
  } else if (state == STATE.FAQ_RENDER) {
    const FAQSet = selectedWorkflow!.faqSet!;
    content = (
      <>
        <H4>{t('faqs')}</H4>

        <FAQSet />

        <Text>{t('faqQuestion')}</Text>

        <Flex flexDirection="row" justifyContent="space-between">
          <Flex gridGap="S200" flexDirection="row" justifyContent="left">
            <Button onClick={() => {
              setState(previousStates.pop()!);
            }}>
              {t('back')}
            </Button>
          </Flex>
          <Flex gridGap="S200" flexDirection="row" justifyContent="right">
            <Button data-cy="yesQuestionAnswered" onClick={() => {
              metricSender.generateHelpMeMetrics(userDetails, selectedWorkflow!.helpType, false);
              setFinalStateTitle(t('thanks'));
              setFinalStateText(t('thanksForInquiring'));
              moveState(STATE.DONE);
            }}>
              {t('yesQuestionAnswered')}
            </Button>
            <Button data-cy="noAskMyManager" variant={ButtonVariant.Primary} onClick={() => {
              moveState(STATE.ACTION_GENERATION);
            }}>
              {t('noAskMyManager')}
            </Button>
          </Flex>
        </Flex>
      </>
    );
  } else if (state == STATE.INPUT_RETRIEVAL) {
    content = (
      <>
        <H4>{t('needMoreDetails')}</H4>
        <Text>{t('needMoreDetailsSubtitle')}</Text>
        {inputs}
        <Flex flexDirection="row" justifyContent="space-between">
          <Flex gridGap="S200" flexDirection="row" justifyContent="left">
            <Button onClick={() => {
              setState(previousStates.pop()!);
            }}>
              {t('back')}
            </Button>
          </Flex>
          <Flex gridGap="S200" flexDirection="row" justifyContent="right">
            <Button variant={ButtonVariant.Primary} onClick={() => {
              moveState(STATE.ACTION_GENERATION);
            }}>
              {t('done')}
            </Button>
          </Flex>
        </Flex>
      </>
    );
  } else if (state == STATE.ACTION_GENERATION) {
    content = (
      <>
        <H4>{t('confirmation')}</H4>
        <ul>
          <li><Text><strong>{t('helpType')}:</strong> {t(selectedSection!.name)}</Text></li>
          <li><Text><strong>{t('helpTitle')}:</strong> {t(selectedWorkflow!.text)}</Text></li>
          {inputValues.size > 0 &&
            <li><Text><strong>{t('yourInputs')}:</strong> {Array.from(inputValues.values()).join(', ')}</Text></li>
          }
        </ul>
        <Text>{t('requestHelpConfirmation')}</Text>

        {isActionSubmitting &&
          <Flex gridGap="S200" flexDirection="row" justifyContent="right">
            <Spinner showText loadingText={t('creatingRequest')} />
          </Flex>
        }
        {!isActionSubmitting &&
          <Flex flexDirection="row" justifyContent="space-between">
            <Flex gridGap="S200" flexDirection="row" justifyContent="left">
              <Button onClick={() => {
                setState(previousStates.pop()!);
              }}>
                {t('back')}
              </Button>
            </Flex>
            <Flex gridGap="S200" flexDirection="row" justifyContent="right">
              <Button data-cy="confirm-no" onClick={() => {
                setFinalStateTitle(t('requestCancelled'));
                setFinalStateText(t('requestErrorMessage'));
                moveState(STATE.DONE);
              }}>
                {t('no')}
              </Button>
              <Button data-cy="confirm-yes" variant={ButtonVariant.Primary} onClick={async () => {
                await generateHelpRequest();
                setFinalStateTitle(t('requestSubmitted'));
                setFinalStateText(t(selectedWorkflow!.completionMessage));
                moveState(STATE.DONE);
              }}>
                {t('yes')}
              </Button>
            </Flex>
          </Flex>
        }
      </>
    );
  } else if (state == STATE.DONE) {
    content = (
      <>
        <H4>{finalStateTitle}</H4>
        <Text>{finalStateText}</Text>
        <Flex gridGap="S200" flexDirection="row" justifyContent="right">
          <Button data-cy="request-submitted-done" variant={ButtonVariant.Primary} onClick={() => {
            setPreviousStates([]);
            setInputValues(new Map<string, string>());
            setSelectedSection(undefined);
            setSelectedWorkflow(undefined);
            setFinalStateTitle("");
            setFinalStateText("");
            setState(STATE.INITIAL);
            props.onRequestCreated();
          }}>
            {t('done')}
          </Button>
        </Flex>
      </>
    );
  }

  return(
    <>
      <Spacer height={16}/>
      <Flex flexDirection="column" gridGap="S300">
        {content}
      </Flex>
    </>
  );
}
